import FilesCard from '@components/cards/files/Files.card';
import { Close } from '@mui/icons-material';
import InventoryIcon from '@mui/icons-material/Inventory';
import { CircularProgress, Divider, Drawer, IconButton, Stack, Typography, styled } from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';
import { AiScheduleObj, AuditOperation, AuditOperationScheduleDto, AuditOwnedByEnum } from '../../../../../store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '../../../../../store/audits/audits-operation/audits-operation.service';
import { NumeralDayOfMonth, WeekDay, getNumeralDayOfMonthTranslation, getWeekDayListFromString, getWeekDayTranslation } from '../../../../../store/common/schedule.model';
import { getTypeOf } from '../../../../../utils/getTypeOf';

const DividerColored = styled(Divider)({
  border: '1px solid rgba(134, 143, 156, 0.18)',
});

interface ContainerItem {
  id: number;
  name: string;
  type: string;
  quantity: number;
  property: AuditOwnedByEnum;
  localisation: string;
  wasteName: string;
  typology: string;
  volume: number;
  unit: string;
  frequency: string;
  days: string;
}

const ContainerDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const { id } = useParams();
  const handleClose = () => {
    navigate(-1);
  };

  const [auditOperation, setAuditOperation] = useState<AuditOperation>({} as AuditOperation);
  const [frequency, setFrequency] = useState<AiScheduleObj>();

  useEffect(() => {
    if (auditOperation?.schedule)
      handleDisplayFrequency(getTypeOf<AuditOperationScheduleDto>(auditOperation.schedule));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
    , [auditOperation?.schedule, auditOperation]);

  const handleDisplayFrequency = (value: Partial<AuditOperationScheduleDto>) => {
    const frequency: AiScheduleObj = {
      frequencyType: value.frequency as string,
      frequencyItem: [{
        title: 'fréquence',
      },
      {
        title: 'jours',
      }]
    };

    const oldValueItem: AuditOperationScheduleDto | string = auditOperation?.schedule ? getTypeOf<AuditOperationScheduleDto>(auditOperation.schedule) : '-';

    if (value) {
      switch (value.frequency) {
        case 'DAILY':
          frequency.frequencyItem[0].value = t('audits.preco.period-week', { value: value.dailyFrequencyNumber });
          frequency.frequencyItem[0].oldValue = _.isObject(oldValueItem) ? t('audits.preco.period', { value: oldValueItem.dailyFrequencyNumber?.toString() }) : oldValueItem;
          frequency.frequencyItem[1].value = '-';
          frequency.frequencyItem[1].oldValue = _.isObject(oldValueItem) ? oldValueItem.dailyEveryWeekDay ? 5 : 0 : oldValueItem;
          break;
        case 'WEEKLY':
          frequency.frequencyItem[0].value = t('audits.preco.period-hebdo', { value: value.weeklyFrequencyNumber });
          frequency.frequencyItem[0].oldValue = _.isObject(oldValueItem) ? oldValueItem.weeklyFrequencyNumber : oldValueItem;
          frequency.frequencyItem[1].value = getWeekDayListFromString(value.daysOfWeek ?? '');
          frequency.frequencyItem[1].oldValue = _.isObject(oldValueItem) ? oldValueItem.daysOfWeek : oldValueItem;
          break;
        case 'MONTHLY':
          frequency.frequencyItem[0].value = t('audits.preco.period-monthly-month', { value: value.monthlyFrequencyNumber });
          frequency.frequencyItem[0].oldValue = _.isObject(oldValueItem) ? oldValueItem.monthlyFrequencyNumber : oldValueItem;
          frequency.frequencyItem[1].value = value.dayOfMonth ? t('audits.preco.period-monthly-day', { value: value.dayOfMonth }) : `${getNumeralDayOfMonthTranslation(value.numeralDayOfMonth ?? NumeralDayOfMonth.FIRST)} ${getWeekDayTranslation(value.weekDayForMonth ?? WeekDay.MONDAY)}`;
          frequency.frequencyItem[1].oldValue = _.isObject(oldValueItem) ? oldValueItem.dayOfMonth : oldValueItem;
          break;
      }
    }
    setFrequency(frequency);
  };

  useEffect(() => {
    setLoading(true);
    auditsOperationService.getAuditOperation(Number(id))
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: setAuditOperation,
      });
  }, [id]);

  const [container, setContainer] = useState<Partial<ContainerItem>>({});

  useEffect(() => {
    if (auditOperation.type == 'CLASSIC') {
      setContainer({
        id: auditOperation.id,
        name: auditOperation?.container?.name || '',
        type: auditOperation?.type || '',
        quantity: auditOperation?.quantity || 0,
        property: auditOperation?.ownedBy || '',
        localisation: auditOperation?.description || '',
        wasteName: auditOperation?.waste?.name || '',
        typology: auditOperation.waste?.family?.name || 'Typologie',
        volume: auditOperation.wasteQuantity || 0,
        unit: auditOperation.wasteUnitName || '',
        frequency: frequency?.frequencyItem[0].value as string ?? frequency?.frequencyItem[0].oldValue as string ?? '',
        days: frequency?.frequencyItem[1].value as string ?? frequency?.frequencyItem[1].oldValue as string ?? ''
      });
    } else {
      setContainer({
        id: auditOperation.id,
        name: auditOperation.containerCustom || '',
        type: auditOperation.type || '',
        quantity: auditOperation.quantity || 0,
        property: auditOperation.ownedBy || '',
        localisation: auditOperation.description || '',
        wasteName: auditOperation.wasteCustom || '',
        typology: auditOperation.waste?.family?.name || 'Typologie',
        volume: auditOperation.wasteQuantity || 0,
        unit: auditOperation.wasteUnitName || '',
        frequency: frequency?.frequencyItem[0].value as string ?? frequency?.frequencyItem[0].oldValue as string ?? '',
        days: frequency?.frequencyItem[1].value as string ?? frequency?.frequencyItem[1].oldValue as string ?? ''
      });
    }
  }, [auditOperation, frequency]);

  return (
    <Drawer PaperProps={{ sx: { paddingInline: 0 } }} anchor="right" open onClose={handleClose}>
      {loading ? (
        <Stack p="20px" height={'100%'} spacing={4} justifyContent={loading ? 'center' : undefined}>
          <CircularProgress size={40} sx={{ alignSelf: 'center' }} />
        </Stack>
      ) : (
        <>
          <Stack px='20px' mb={'40px'}>
            <Stack direction="row" justifyContent="flex-end">
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </Stack>
            <Stack>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Stack sx={{
                  backgroundColor: '#4F3FF5',
                  borderRadius: '3px',
                  alignItems: 'center',
                  padding: '4px',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px'
                }}>
                  <InventoryIcon sx={{ color: 'white', width: '15px' }} />
                </Stack>
                <Typography fontSize={18} fontWeight="700" mb={'25px'}>
                  {container.name}
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" justifyContent='space-between' mt='45px' mb='16px'>
                {[
                  { title: 'type', value: container.name },
                  { title: 'quantité', value: container.quantity },
                  { title: 'propriété', value: container.property }
                ].map((item) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontSize="14px" fontWeight={400} color="#868F9C">
                      {item.title}
                    </Typography>
                    <Stack p='7px 11px' bgcolor='#E9EBEE'>
                      <Typography fontSize="12px" fontWeight={400} color="#192133">
                        {item.value || '-'}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}

              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} mb='8px'>
                <Typography fontSize="14px" fontWeight={400} color="#868F9C">
                  localisation
                </Typography>
                <Stack p='7px 11px' bgcolor='#E9EBEE'>
                  <Typography fontSize="12px" fontWeight={400} color="#192133">
                    {container.localisation || '-'}
                  </Typography>
                </Stack>
              </Stack>
              <DividerColored />
              <Stack mt='16px' mb='16px' spacing={2}>
                <Typography fontSize='11px' fontWeight={500} color='#192133'>
                  Déchet
                </Typography>
                <Stack direction="row" flexWrap="wrap" justifyContent='space-between' mb='16px'>
                  {[
                    { title: 'déchet', value: container.wasteName },
                    { title: 'typologie', value: container.typology },
                    { title: 'volume', value: container.volume }
                  ].map((item) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize="14px" fontWeight={400} color="#868F9C">
                        {item.title}
                      </Typography>
                      <Stack p='7px 11px' bgcolor='#E9EBEE'>
                        <Typography fontSize="12px" fontWeight={400} color="#192133">
                          {item.value || '-'}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} mt='20px'>
                  <Typography fontSize="14px" fontWeight={400} color="#868F9C">
                    unité
                  </Typography>
                  <Stack p='7px 11px' bgcolor='#E9EBEE'>
                    <Typography fontSize="12px" fontWeight={400} color="#192133">
                      {container.unit || '-'}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              <DividerColored />
              <Stack mt='16px' mb='16px' spacing={2}>
                <Typography fontSize='11px' fontWeight={500} color='#192133'>
                  Ramassage
                </Typography>
                <Stack direction="row" flexWrap="wrap" justifyContent='space-between' mb='16px'>
                  {[
                    { title: 'fréquence', value: container.frequency },
                    { title: 'jours', value: container.days },
                  ].map((item) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography fontSize="14px" fontWeight={400} color="#868F9C">
                        {item.title}
                      </Typography>
                      <Stack p='7px 11px' bgcolor='#E9EBEE'>
                        <Typography fontSize="12px" fontWeight={400} color="#192133">
                          {item.value || '-'}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
              <DividerColored />
              <Stack direction='row' pt={'25px'} spacing={2}>
                {auditOperation.documents?.map(id => (
                  <FilesCard document={{
                    id: id,
                    name: '',
                    description: '',
                    documentKind: '',
                    filename: '',
                    mimetype: 'image/png',
                  }} />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </Drawer >
  );
};

export default ContainerDetails;